// ONE code-owned message for the canonical price-change status (round 4, D3).
// Both the Price Summary band's dollar tile and the YoY table header render
// THIS string when the owner says the headline is honestly insufficient, so
// the page can never argue with itself. `applied` renders figures and no
// message; `not_applicable` / `operational_failure` keep each surface's
// existing generic absence copy.
//
// Lane 1414 (Ryan, 2026-10-17: "Just flag it but still show it"): with the
// server's low-coverage switch on, the status carries `low_coverage` (on every
// applied figure) or `no_overlap` (nothing bought in both periods). With the
// switch off neither key is sent, so every helper below returns null and each
// surface renders exactly as before.
function lowCoverageSwitchOn(status) {
  return !!status && typeof status === 'object'
    && ('low_coverage' in status || status.no_overlap === true
        || status.low_coverage_enabled === true);
}

export function priceChangeStatusMessage(status, fallbackMonths) {
  const months = (status && status.months) || fallbackMonths || 12;
  if (lowCoverageSwitchOn(status)) {
    if (status.no_overlap) return `No ${months}-month price change: no part was bought in both periods.`;
    if (status.status === 'operational_failure') return 'Price change unavailable right now.';
  }
  if (!status || status.status !== 'honestly_insufficient') return null;
  const measured = [];
  if (typeof status.parts_compared === 'number' && status.parts_compared > 0)
    measured.push(`${status.parts_compared} part${status.parts_compared === 1 ? '' : 's'} compare`);
  if (typeof status.spend_compared_usd === 'number' && status.spend_compared_usd > 0)
    measured.push(`$${Math.round(status.spend_compared_usd).toLocaleString('en-US')} of matched spend`);
  const reason = status.reason || 'not enough matched purchase history for this window';
  const detail = measured.length ? ` (${measured.join(', ')})` : '';
  return `No ${months}-month price-change headline: ${reason}${detail}. Rows below are per-part detail, not a headline.`;
}

// Lane 1414: the short absence that replaces the buyer's price change (and the
// gap built on it) in a tile. Null unless the switch is on and there is no
// figure; the market side keeps its own readiness either way.
export function priceChangeAbsence(status) {
  if (!lowCoverageSwitchOn(status)) return null;
  if (status.no_overlap) return 'Not enough purchase history';
  if (status.status === 'operational_failure') return 'Price change unavailable right now';
  return null;
}

// Lane 1414: the one-line flag beside a figure measured on few parts or a small
// share of spend. Facts, not a score: "Low coverage: 2 parts, 18% of spend".
export function priceChangeLowCoverageLine(status) {
  if (!status || status.status !== 'applied' || status.low_coverage !== true) return null;
  // The server's words (one wording for chat, decks and this page).
  if (typeof status.low_coverage_text === 'string' && status.low_coverage_text) return status.low_coverage_text;
  const facts = [];
  if (typeof status.parts_compared === 'number')
    facts.push(`${status.parts_compared} part${status.parts_compared === 1 ? '' : 's'}`);
  if (typeof status.coverage_pct === 'number')
    facts.push(`${Math.round(status.coverage_pct)}% of spend`);
  return facts.length ? `Low coverage: ${facts.join(', ')}` : 'Low coverage';
}

// Lane 1415: with each part measured against its own market index, the market
// figure covers the compared parts that have an index. Null when every compared
// part has one, or when the market is not built from part indexes.
export function partIndexCoverageLine(blend) {
  if (!blend || blend.market_basis !== 'part_index') return null;
  const cov = blend.market_coverage_pct;
  if (typeof cov !== 'number' || cov >= 99.5) return null;
  return `Market index covers ${Math.round(cov)}% of the compared parts' spend`;
}
